import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Table,
  TableBody,
  TablePagination,
  Paper,
  TextField,
  InputAdornment,
  Switch,
  Tooltip,
  TableSortLabel,
  Typography,
} from "@mui/material";
import FeatherIcon from "feather-icons-react";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  GetApplicationList,
  GetApplicationsModulesList,
  AddApplicationsModules,
  UpdateApplicationsModules,
} from "../../../services/admin";
import CustomFormLabel from "../../../components/forms/custom-elements/CustomFormLabel";
import CustomTextField from "../../../components/forms/custom-elements/CustomTextField";

const AddModules = () => {
  const [applications, setApplications] = useState([]);

  const [list, setList] = useState([]);

  const [editId, setEditId] = useState(null);

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [globalSearch, setGlobalSearch] = useState("");

  const [showAllFilters, setShowAllFilters] = useState(false);

  const [searchText, setSearchText] = useState({});

  const [order, setOrder] = useState("asc");

  const [orderBy, setOrderBy] = useState("");

  useEffect(() => {
    fetchApplications();

    fetchModules();
  }, []);

  // =========================================
  // FETCH APPLICATIONS
  // =========================================

  const fetchApplications = async () => {
    try {
      const response = await GetApplicationList();

      const formattedApps = (response?.data || []).map((item) => ({
        application_ID: item.application_ID || item.Application_ID,

        application_Name: item.application_Name || item.Application_Name,

        IsActive:
          item.isActive === true ||
          item.isActive === 1 ||
          item.isActive === "1" ||
          item.IsActive === true ||
          item.IsActive === 1 ||
          item.IsActive === "1",
      }));

      const activeApps = formattedApps.filter((x) => x.IsActive === true);

      setApplications(activeApps);
    } catch (error) {
      console.log(error);

      toast.error("Failed to load applications");
    }
  };

  // =========================================
  // FETCH MODULES
  // =========================================

  const fetchModules = async () => {
    try {
      const response = await GetApplicationsModulesList();

      const formattedData = (response?.data || []).map((item) => ({
        module_ID: item.Module_ID || item.module_ID,

        application_ID: item.Application_ID || item.application_ID,

        application_Name: item.Application_Name || item.application_Name,

        module_Name: item.Module_Name || item.module_Name,

        IsActive: Boolean(item.IsActive ?? item.isActive),
      }));

      setList(formattedData);
    } catch (error) {
      console.log(error);

      toast.error("Failed to load modules");
    }
  };

  // =========================================
  // VALIDATION
  // =========================================

  const validationSchema = Yup.object({
    application_ID: Yup.number()
      .typeError("Application is required")
      .required("Application is required"),

    module_Name: Yup.string()
      .trim()
      .required("Module name is required")
      .matches(/^[A-Za-z0-9\s&_()-]+$/, "Invalid module name")
      .test(
        "duplicate-module",
        "Module already exists for this application",
        function (value) {
          if (!value) return true;

          const isDuplicate = list.some(
            (item) =>
              item.application_ID === this.parent.application_ID &&
              item.module_Name?.toLowerCase().trim() ===
                value.toLowerCase().trim() &&
              item.module_ID !== editId,
          );

          return !isDuplicate;
        },
      ),
  });

  // =========================================
  // FORMIK
  // =========================================

  const formik = useFormik({
    initialValues: {
      application_ID: "",

      module_Name: "",

      IsActive: true,
    },

    validationSchema,

    onSubmit: async (values, { resetForm }) => {
      try {
        const payload = {
          Module_ID: editId || 0,

          Application_ID: values.application_ID,

          Module_Name: values.module_Name.trim(),

          IsActive: values.IsActive ? 1 : 0,
        };

        if (editId) {
          await UpdateApplicationsModules(payload);

          toast.success("Module updated successfully");
        } else {
          await AddApplicationsModules(payload);

          toast.success("Module added successfully");
        }

        fetchModules();

        resetForm();

        setEditId(null);
      } catch {
        toast.error("Save failed");
      }
    },
  });

  // =========================================
  // EDIT
  // =========================================

  const handleEdit = (item) => {
    setEditId(item.module_ID);

    formik.setValues({
      application_ID: item.application_ID,

      module_Name: item.module_Name,

      IsActive: item.IsActive,
    });

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =========================================
  // RESET
  // =========================================

  const resetForm = () => {
    setEditId(null);

    formik.resetForm();
  };

  // =========================================
  // TOGGLE STATUS
  // =========================================

  const handleToggleStatus = async (item, checked) => {
    setList((prev) =>
      prev.map((x) =>
        x.module_ID === item.module_ID
          ? {
              ...x,
              IsActive: checked,
            }
          : x,
      ),
    );

    try {
      const payload = {
        Module_ID: item.module_ID,

        Application_ID: item.application_ID,

        Module_Name: item.module_Name,

        IsActive: checked ? 1 : 0,
      };

      await UpdateApplicationsModules(payload);

      toast.success(`Module ${checked ? "Activated" : "Deactivated"}`);
    } catch {
      toast.error("Failed to update status");

      setList((prev) =>
        prev.map((x) =>
          x.module_ID === item.module_ID
            ? {
                ...x,
                IsActive: !checked,
              }
            : x,
        ),
      );
    }
  };

  // =========================================
  // SORT
  // =========================================

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";

    setOrder(isAsc ? "desc" : "asc");

    setOrderBy(property);
  };

  // =========================================
  // RESET FILTER
  // =========================================

  const handleResetFilters = () => {
    setGlobalSearch("");

    setSearchText({});

    setShowAllFilters(false);

    setPage(0);
  };

  // =========================================
  // FILTER
  // =========================================

  const filteredList = list.filter((item) => {
    const globalMatch = Object.values(item)
      .join(" ")
      .toLowerCase()
      .includes(globalSearch.toLowerCase());

    const columnMatch = Object.keys(searchText).every((key) => {
      if (!searchText[key]) return true;

      const value = item[key]?.toString().toLowerCase() || "";

      return value.includes(searchText[key].toLowerCase());
    });

    return globalMatch && columnMatch;
  });

  // =========================================
  // SORTED LIST
  // =========================================

  const sortedList = [...filteredList].sort((a, b) => {
    if (!orderBy) return 0;

    let valueA = a[orderBy];

    let valueB = b[orderBy];

    if (typeof valueA === "string") valueA = valueA.toLowerCase();

    if (typeof valueB === "string") valueB = valueB.toLowerCase();

    if (valueA < valueB) return order === "asc" ? -1 : 1;

    if (valueA > valueB) return order === "asc" ? 1 : -1;

    return 0;
  });

  // =========================================
  // HEADER
  // =========================================

  const renderHeader = (label, field) => (
    <TableCell>
      <Box
        display="flex"
        alignItems="center"
        gap={0.5}
        sx={{
          color: "text.primary",
        }}
      >
        <TableSortLabel
          active={orderBy === field}
          direction={orderBy === field ? order : "asc"}
          onClick={() => handleSort(field)}
        >
          {label}
        </TableSortLabel>
      </Box>
    </TableCell>
  );

  return (
    <Box>
      {/* HEADER */}

      <Card
        sx={{
          mb: 2,
          p: 0,
          overflow: "hidden",
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          px={2}
          py={1.5}
          sx={{
            backgroundColor: "primary.light",
            color: "primary.main",
          }}
        >
          <FeatherIcon icon="alert-circle" width="18" />

          <Box sx={{ ml: 1 }}>{editId ? "Edit Module" : "Add Module"}</Box>
        </Box>

        {/* FORM */}

        <CardContent
          sx={{
            padding: "10px 30px",
          }}
        >
          <Grid container spacing={2} alignItems="flex-start">
            {/* APPLICATION */}

            <Grid item lg={3} md={12} sm={12}>
              <CustomFormLabel>Application</CustomFormLabel>

              <CustomTextField
                select
                fullWidth
                size="small"
                name="application_ID"
                value={formik.values.application_ID}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                SelectProps={{
                  native: true,
                }}
                error={
                  formik.touched.application_ID &&
                  Boolean(formik.errors.application_ID)
                }
                helperText={
                  formik.touched.application_ID && formik.errors.application_ID
                }
              >
                <option value="">--Select Application--</option>

                {applications.map((item) => (
                  <option key={item.application_ID} value={item.application_ID}>
                    {item.application_Name}
                  </option>
                ))}
              </CustomTextField>
            </Grid>

            {/* MODULE NAME */}

            <Grid item lg={3} md={12} sm={12}>
              <CustomFormLabel>Module Name</CustomFormLabel>

              <CustomTextField
                name="module_Name"
                placeholder="Enter Module Name"
                value={formik.values.module_Name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                size="small"
                error={
                  formik.touched.module_Name &&
                  Boolean(formik.errors.module_Name)
                }
                helperText={
                  formik.touched.module_Name && formik.errors.module_Name
                }
              />
            </Grid>

            {/* STATUS */}

            <Grid item lg={2} md={12} sm={12}>
              <CustomFormLabel>Status</CustomFormLabel>

              <Switch
                checked={formik.values.IsActive}
                onChange={(e) =>
                  formik.setFieldValue("IsActive", e.target.checked)
                }
              />
            </Grid>

            {/* BUTTON */}

            <Grid
              item
              lg={2}
              md={12}
              sm={12}
              mt={5}
              display="flex"
              alignItems="flex-start"
              height="100%"
            >
              <Button
                variant="contained"
                onClick={formik.handleSubmit}
                disabled={!formik.isValid}
              >
                {editId ? "Update" : "Add"}
              </Button>

              <Button sx={{ ml: 2 }} variant="outlined" onClick={resetForm}>
                {editId ? "Cancel" : "Reset"}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* TABLE */}

      <Card
        sx={{
          mt: 2,
          p: 0,
          overflow: "hidden",
        }}
      >
        {/* HEADER */}

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={2}
          py={1.5}
          sx={{
            backgroundColor: "primary.light",
            color: "primary.main",
          }}
        >
          {/* LEFT */}

          <Box display="flex" alignItems="center">
            <FeatherIcon icon="alert-circle" width="18" />

            <Box sx={{ ml: 1 }}>Existing Modules</Box>
          </Box>

          {/* RIGHT */}

          <Box display="flex" alignItems="center">
            <TextField
              size="small"
              placeholder="Search..."
              value={globalSearch}
              onFocus={() => {
                setShowAllFilters(false);

                setSearchText({});
              }}
              onChange={(e) => {
                setGlobalSearch(e.target.value);

                setPage(0);
              }}
              sx={{
                width: 220,
                backgroundColor: "white",
                borderRadius: 1,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            {globalSearch && (
              <CloseIcon
                sx={{
                  cursor: "pointer",
                  ml: 1,
                }}
                onClick={() => setGlobalSearch("")}
              />
            )}

            <FilterAltIcon
              sx={{
                cursor: "pointer",
                ml: 2,
                color: showAllFilters
                  ? (theme) => theme.palette.primary.main
                  : "text.primary",
                transition: "0.2s",
              }}
              onClick={() => setShowAllFilters(!showAllFilters)}
            />

            <Button
              variant="outlined"
              size="small"
              sx={{
                ml: 2,
                height: 32,
              }}
              onClick={handleResetFilters}
            >
              Reset Filter
            </Button>
          </Box>
        </Box>

        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#FAFBFB",
                  }}
                >
                  {renderHeader("ID", "module_ID")}

                  {renderHeader("Application", "application_Name")}

                  {renderHeader("Module Name", "module_Name")}

                  <TableCell>Status</TableCell>

                  <TableCell align="center">Action</TableCell>
                </TableRow>

                {showAllFilters && (
                  <TableRow>
                    {["module_ID", "application_Name", "module_Name"].map(
                      (field) => (
                        <TableCell key={field}>
                          <TextField
                            size="small"
                            fullWidth
                            value={searchText[field] || ""}
                            onChange={(e) =>
                              setSearchText({
                                ...searchText,
                                [field]: e.target.value,
                              })
                            }
                            placeholder="Search..."
                            InputProps={{
                              endAdornment: searchText[field] && (
                                <InputAdornment position="end">
                                  <CloseIcon
                                    fontSize="small"
                                    sx={{
                                      cursor: "pointer",
                                    }}
                                    onClick={() =>
                                      setSearchText({
                                        ...searchText,
                                        [field]: "",
                                      })
                                    }
                                  />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </TableCell>
                      ),
                    )}

                    <TableCell />

                    <TableCell />
                  </TableRow>
                )}
              </TableHead>

              <TableBody>
                {sortedList.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No modules found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {(rowsPerPage > 0
                  ? sortedList.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage,
                    )
                  : sortedList
                ).map((item) => (
                  <TableRow key={item.module_ID}>
                    <TableCell>{item.module_ID}</TableCell>

                    <TableCell>{item.application_Name}</TableCell>

                    <TableCell>{item.module_Name}</TableCell>

                    <TableCell>
                      <Tooltip title={item.IsActive ? "Active" : "Inactive"}>
                        <Switch
                          checked={item.IsActive}
                          size="small"
                          onChange={(e) =>
                            handleToggleStatus(item, e.target.checked)
                          }
                        />
                      </Tooltip>
                    </TableCell>

                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <EditIcon
                          sx={{
                            cursor: "pointer",
                            color: "primary.main",
                          }}
                          onClick={() => handleEdit(item)}
                        />
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={sortedList.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));

              setPage(0);
            }}
            showFirstButton
            showLastButton
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default AddModules;
